import { forwardRef } from 'react';
import { Shield, FileCheck, Lock } from 'lucide-react';
import { SectionAtmosphere, TYPE_SECTION_EYEBROW } from '../layout/HoldingBackdrop';

const Security = forwardRef<HTMLElement>((_, ref) => {
  return (
    <section ref={ref} id="security" className="relative isolate overflow-hidden px-5 py-24 md:px-8 md:py-32">
      <SectionAtmosphere variant="security" />
      <div className="relative z-10 mx-auto max-w-7xl">
        <div className="animate-on-scroll mx-auto mb-16 max-w-3xl text-center">
          <p className={TYPE_SECTION_EYEBROW}>Engineering</p>
          <h2 className="mt-5 text-4xl font-semibold tracking-[-0.04em] text-[#f1f5ec]/90 md:text-5xl">
            Security
          </h2>
          <p className="mt-6 text-base leading-8 text-[#d6ddcf]/85 md:text-lg">
            Fidus Finance is a software company in Florianópolis. These are the controls we use to
            protect plant data and keep operational records checkable.
          </p>
        </div>

        <div className="mb-12 grid gap-6 md:grid-cols-2 md:gap-5">
          <div className="animate-on-scroll rounded-3xl border border-[#e8ede4]/10 bg-black/20 p-8 backdrop-blur-md">
            <Shield className="mb-4 h-10 w-10 text-teal-200/85" aria-hidden />
            <h3 className="text-2xl font-semibold tracking-[-0.03em] text-[#f1f5ec]/88">Access and encryption</h3>
            <p className="mt-4 leading-7 text-[#d6ddcf]/85">
              Engineering controls for who can see plant data, and encryption for the platforms we
              build in Florianópolis.
            </p>
            <ul className="mt-6 space-y-3 text-[#d6ddcf]/85">
              <li className="flex items-start gap-3">
                <FileCheck className="mt-1 h-5 w-5 flex-shrink-0 text-teal-200/80" aria-hidden />
                <span className="leading-relaxed">Role-based access for operator teams</span>
              </li>
              <li className="flex items-start gap-3">
                <FileCheck className="mt-1 h-5 w-5 flex-shrink-0 text-teal-200/80" aria-hidden />
                <span className="leading-relaxed">End-to-end encryption (AES-256 baseline)</span>
              </li>
              <li className="flex items-start gap-3">
                <FileCheck className="mt-1 h-5 w-5 flex-shrink-0 text-teal-200/80" aria-hidden />
                <span className="leading-relaxed">Multi-factor authentication and single sign-on</span>
              </li>
            </ul>
          </div>

          <div className="animate-on-scroll rounded-3xl border border-[#e8ede4]/10 bg-black/20 p-8 backdrop-blur-md">
            <FileCheck className="mb-4 h-10 w-10 text-teal-200/85" aria-hidden />
            <h3 className="text-2xl font-semibold tracking-[-0.03em] text-[#f1f5ec]/88">Records you can check</h3>
            <p className="mt-4 leading-7 text-[#d6ddcf]/85">
              Generation and operational data can be anchored so a later review can see whether a
              record changed.
            </p>
            <ul className="mt-6 space-y-3 text-[#d6ddcf]/85">
              <li className="flex items-start gap-3">
                <FileCheck className="mt-1 h-5 w-5 flex-shrink-0 text-teal-200/80" aria-hidden />
                <span className="leading-relaxed">Blockchain-anchored audit trail</span>
              </li>
              <li className="flex items-start gap-3">
                <FileCheck className="mt-1 h-5 w-5 flex-shrink-0 text-teal-200/80" aria-hidden />
                <span className="leading-relaxed">Tamper-evident generation records</span>
              </li>
              <li className="flex items-start gap-3">
                <FileCheck className="mt-1 h-5 w-5 flex-shrink-0 text-teal-200/80" aria-hidden />
                <span className="leading-relaxed">Logs operators can export for review</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="animate-on-scroll rounded-[2rem] border border-teal-200/25 bg-white/[0.04] p-8 backdrop-blur-md md:p-10">
          <div className="mx-auto max-w-3xl">
            <div className="mb-6 flex items-center gap-4">
              <Lock className="h-8 w-8 flex-shrink-0 text-teal-200/90" aria-hidden />
              <h3 className="text-2xl font-semibold tracking-[-0.03em] text-[#f1f5ec]/90">
                How the platform is looked after
              </h3>
            </div>
            <p className="mb-6 leading-7 text-[#d6ddcf]/85">
              Day-to-day protection for plant data, operator workflows and audit trails:
            </p>
            <div className="grid gap-6 text-[#d6ddcf]/85 md:grid-cols-2">
              <ul className="space-y-3">
                <li className="flex items-center gap-3">
                  <FileCheck className="h-5 w-5 flex-shrink-0 text-teal-200/80" aria-hidden />
                  <span>Privacy controls for plant and operator data</span>
                </li>
                <li className="flex items-center gap-3">
                  <FileCheck className="h-5 w-5 flex-shrink-0 text-teal-200/80" aria-hidden />
                  <span>Independent security reviews</span>
                </li>
                <li className="flex items-center gap-3">
                  <FileCheck className="h-5 w-5 flex-shrink-0 text-teal-200/80" aria-hidden />
                  <span>Scheduled internal security checks</span>
                </li>
              </ul>
              <ul className="space-y-3">
                <li className="flex items-center gap-3">
                  <FileCheck className="h-5 w-5 flex-shrink-0 text-teal-200/80" aria-hidden />
                  <span>Monitoring and incident response</span>
                </li>
                <li className="flex items-center gap-3">
                  <FileCheck className="h-5 w-5 flex-shrink-0 text-teal-200/80" aria-hidden />
                  <span>Backups of operational records</span>
                </li>
                <li className="flex items-center gap-3">
                  <FileCheck className="h-5 w-5 flex-shrink-0 text-teal-200/80" aria-hidden />
                  <span>Access removed when a role changes</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
});

Security.displayName = 'Security';
export default Security;
